/**
 * Brush-driven level of detail for the explore timeseries.
 *
 * The page loads the whole day at 1 Hz (the base). A BRUSH zoom brings in finer data for the brushed
 * stretch; clearing the zoom (double-click reset, selection clear, zooming back out to the whole day)
 * goes back to the 1 Hz base. Nothing loads on wheel zoom or pan.
 *
 *   brushed span ≥ 5 min  → base only (1 Hz)
 *   brushed span < 5 min  → 10 Hz for the brushed range ± half its span — never more than 10 min
 *
 * A brush never fetches RAW: SailGP raw data is 10 Hz, so a RAW step would refetch the same samples
 * (Alinghi's ladder adds RAW under 5 min). The RAW tier stays in the type so a ladder entry can bring it back.
 *
 * The controller owns no data shape: `fetchWindow` returns whatever the page splices (`T`), and `onChange`
 * hands back the windows to show. The last LOD_CACHE_PER_TIER windows per tier are kept, so brushing the
 * same stretch again does not refetch.
 */
import { debug } from './console';

export type LodTier = '10HZ' | 'RAW';

export interface LodTierSpec {
  tier: LodTier;
  /** A brush narrower than this loads the tier. */
  showBelowMs: number;
  /** Longest window fetched for the tier. */
  maxWindowMs: number;
}

const MINUTE_MS = 60_000;

/** Coarsest first. */
export const LOD_TIERS: readonly LodTierSpec[] = [
  { tier: '10HZ', showBelowMs: 5 * MINUTE_MS, maxWindowMs: 10 * MINUTE_MS },
];

/** Hard ceiling on a RAW window: never pull more than 10 min of full-rate data. */
export const LOD_RAW_MAX_WINDOW_MS = 10 * MINUTE_MS;
export const LOD_CACHE_PER_TIER = 2;
/** A view at least this share of the loaded day counts as "zoomed back out" → base only. */
export const LOD_FULL_VIEW_FRACTION = 0.98;

export interface LodWindow<T> {
  tier: LodTier;
  startMs: number;
  endMs: number;
  /** null when the fetch failed or returned nothing. */
  payload: T | null;
}

export interface TimeseriesLodControllerOptions<T> {
  /** Finest tier this chart's series asked for (a 10HZ-only chart never fetches RAW). */
  maxTier: LodTier;
  /** Loaded day extent in epoch ms; windows are clamped into it. */
  domain: () => { minMs: number; maxMs: number } | null;
  fetchWindow: (tier: LodTier, startMs: number, endMs: number, signal: AbortSignal) => Promise<T | null>;
  /**
   * Windows to show (empty = 1 Hz base). Called only when that set changes. May return a promise that
   * settles once the page has the rebuilt data in hand — `prepare` waits on it.
   */
  onChange: (windows: LodWindow<T>[]) => void | Promise<void>;
  label?: string;
}

export function tierRank(t: LodTier): number {
  return t === '10HZ' ? 1 : 2;
}

/** Tier a brush of `spanMs` needs (finest allowed), or null for the 1 Hz base. */
export function lodTierForSpan(spanMs: number, maxTier: LodTier): LodTierSpec | null {
  let pick: LodTierSpec | null = null;
  for (const spec of LOD_TIERS) {
    if (tierRank(spec.tier) > tierRank(maxTier)) {continue;}
    if (spanMs < spec.showBelowMs) {pick = spec;}
  }
  return pick;
}

/** Window for a brush [minMs, maxMs]: the range ± half its span, capped, centred, clamped into `domain`. */
export function lodWindowFor(
  spec: LodTierSpec,
  minMs: number,
  maxMs: number,
  domain: { minMs: number; maxMs: number } | null,
): { startMs: number; endMs: number } {
  const span = maxMs - minMs;
  const cap = spec.tier === 'RAW' ? Math.min(spec.maxWindowMs, LOD_RAW_MAX_WINDOW_MS) : spec.maxWindowMs;
  const len = Math.min(span * 2, cap);
  const center = (minMs + maxMs) / 2;
  let startMs = center - len / 2;
  let endMs = center + len / 2;
  if (domain && domain.maxMs > domain.minMs) {
    if (endMs > domain.maxMs) {
      endMs = domain.maxMs;
      startMs = endMs - len;
    }
    if (startMs < domain.minMs) {
      startMs = domain.minMs;
      endMs = Math.min(domain.maxMs, startMs + len);
    }
  }
  return { startMs, endMs };
}

export class TimeseriesLodController<T> {
  private readonly opts: TimeseriesLodControllerOptions<T>;
  private readonly cache = new Map<LodTier, LodWindow<T>[]>();
  private inFlight: AbortController | null = null;
  private active: LodWindow<T>[] = [];
  private disposed = false;

  constructor(opts: TimeseriesLodControllerOptions<T>) {
    this.opts = opts;
  }

  /**
   * A brush zoom to [minMs, maxMs] is about to land: load what it needs and hand it to the page.
   * Resolves true when the shown data changed (the caller waits for it to reach the plot), false if not.
   */
  async prepare(minMs: number, maxMs: number): Promise<boolean> {
    if (this.disposed || !Number.isFinite(minMs) || !Number.isFinite(maxMs) || !(maxMs > minMs)) {return false;}
    this.inFlight?.abort();
    this.inFlight = null;
    const spec = lodTierForSpan(maxMs - minMs, this.opts.maxTier);
    if (!spec) {return this.show([]);}

    const cached = (this.cache.get(spec.tier) ?? []).find(
      (w) => w.payload !== null && w.startMs <= minMs && w.endMs >= maxMs,
    );
    if (cached) {
      this.remember(cached);
      return this.show([cached]);
    }

    const win = lodWindowFor(spec, minMs, maxMs, this.opts.domain());
    const controller = new AbortController();
    this.inFlight = controller;
    debug('[timeseriesLod] fetch window', { label: this.opts.label, tier: spec.tier, ...win });
    let payload: T | null = null;
    try {
      payload = await this.opts.fetchWindow(spec.tier, win.startMs, win.endMs, controller.signal);
    } catch (err) {
      if (!controller.signal.aborted) {debug('[timeseriesLod] window fetch failed', { tier: spec.tier, err });}
    }
    if (this.disposed || controller.signal.aborted) {return false;}
    this.inFlight = null;
    if (payload === null) {return this.show([]);}
    const w: LodWindow<T> = { tier: spec.tier, startMs: win.startMs, endMs: win.endMs, payload };
    this.remember(w);
    return this.show([w]);
  }

  /**
   * Zoom settled somewhere: back on the whole day means back on the 1 Hz base. `fullView` is the chart's
   * own word for that — its full range is where the series have values, which can be narrower than the
   * loaded rows `domain` spans. Also when nothing is shown yet — a window still in flight (slow network:
   * brush, then double-click before it lands) would otherwise arrive afterwards and leave the whole-day
   * view stuck on 10 Hz.
   */
  onVisibleRange(minMs: number, maxMs: number, fullView = false): void {
    if (this.disposed || (this.active.length === 0 && !this.inFlight)) {return;}
    const d = this.opts.domain();
    const wholeDomain = !!d && d.maxMs > d.minMs && maxMs - minMs >= (d.maxMs - d.minMs) * LOD_FULL_VIEW_FRACTION;
    if (fullView || wholeDomain) {
      this.inFlight?.abort();
      this.inFlight = null;
      void this.show([]);
    }
  }

  /** Windows currently shown (exposed for tests). */
  activeWindows(): LodWindow<T>[] {
    return this.active;
  }

  dispose(): void {
    this.disposed = true;
    this.inFlight?.abort();
    this.inFlight = null;
    this.cache.clear();
    this.active = [];
  }

  private remember(w: LodWindow<T>): void {
    const list = (this.cache.get(w.tier) ?? []).filter((x) => x !== w);
    list.unshift(w);
    this.cache.set(w.tier, list.slice(0, LOD_CACHE_PER_TIER));
  }

  private async show(windows: LodWindow<T>[]): Promise<boolean> {
    const key = (ws: LodWindow<T>[]) => ws.map((w) => `${w.tier}:${w.startMs}-${w.endMs}`).join('|');
    if (key(windows) === key(this.active)) {return false;}
    this.active = windows;
    await this.opts.onChange(windows);
    return true;
  }
}
